import { ChevronDown, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import {
  chipBodyVariants,
  chipDismissClasses,
  chipInnerBodyVariants,
  chipWrapperVariants,
} from './chip.styles';
import type { ChipProps } from './chip.types';

/**
 * Chip — pill-shaped enum representer. Composes as a static label, button,
 * Radix trigger, and/or dismissible tag. Color is driven by entity-* tokens.
 */
const Chip = React.forwardRef<HTMLElement, ChipProps>((props, ref) => {
  const intl = useDesignSystemIntl();
  // ChipProps is a discriminated union over `onClick` / `onDismiss`, so
  // destructuring both at once requires widening the inferred type. The cast
  // is local and confined to this scope.
  const {
    color = 'neutral',
    variant = 'default',
    leadingIcon,
    trailingIcon,
    withChevron = false,
    disabled = false,
    className,
    children,
    onClick,
    onDismiss,
    dismissLabel,
    ...rest
  } = props as ChipProps & {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onDismiss?: () => void;
    dismissLabel?: string;
  };

  // Opt-in chevron. Inside a Radix trigger, Radix mirrors its open state onto
  // the element as `data-state="open"`, so the rotation animation runs without
  // any extra wiring. Consumers who want a different trailing icon can pass
  // `trailingIcon` — it takes precedence over `withChevron`.
  const resolvedTrailingIcon =
    trailingIcon ??
    (withChevron ? (
      <ChevronDown
        className={cn(
          'transition-transform duration-150 motion-reduce:transition-none',
          'group-data-[state=open]/chip:rotate-180'
        )}
      />
    ) : null);

  // Icon slots wrap any ReactNode in a consistent layout box so non-SVG content
  // (avatars, images, indicator dots) gets the same `shrink-0` + vertical-center
  // treatment SVGs already receive via the broad `[&_svg]` selectors in
  // `chip.styles.ts`. SVG auto-sizing still flows through those selectors.
  const bodyContents = (
    <>
      {leadingIcon != null && (
        <span
          data-slot="chip-leading-icon"
          className="inline-flex shrink-0 items-center justify-center"
        >
          {leadingIcon}
        </span>
      )}
      {children}
      {resolvedTrailingIcon != null && (
        <span
          data-slot="chip-trailing-icon"
          className="inline-flex shrink-0 items-center justify-center"
        >
          {resolvedTrailingIcon}
        </span>
      )}
    </>
  );

  // Dismissible chip — wrapper holds the pill chrome; body and X are siblings.
  if (onDismiss !== undefined) {
    const isClickable = onClick !== undefined;
    const BodyComp = isClickable ? 'button' : 'span';
    const resolvedDismissLabel =
      dismissLabel ??
      intl.formatMessage({
        defaultMessage: 'Dismiss',
        id: 'Cl/v71JhXI',
        description:
          'Default accessible label for the chip dismiss button when no dismissLabel prop is supplied',
      });
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        data-slot="chip"
        data-disabled={disabled || undefined}
        className={cn(chipWrapperVariants({ color, variant, disabled }), className)}
        {...(rest as React.HTMLAttributes<HTMLSpanElement>)}
      >
        <BodyComp
          className={chipInnerBodyVariants({ interactive: isClickable })}
          onClick={isClickable ? onClick : undefined}
          disabled={isClickable ? disabled : undefined}
          type={isClickable ? 'button' : undefined}
        >
          {bodyContents}
        </BodyComp>
        <button
          type="button"
          aria-label={resolvedDismissLabel}
          disabled={disabled}
          className={chipDismissClasses}
          onClick={(event) => {
            event.stopPropagation();
            onDismiss();
          }}
        >
          <X />
        </button>
      </span>
    );
  }

  // Interactive chip — single <button>. Triggered by either a direct `onClick`
  // or by being wrapped in a Radix popup trigger. We detect the latter via
  // `aria-haspopup`, which DropdownMenuTrigger / PopoverTrigger / SelectTrigger
  // all merge onto the child via Slot (per WAI-ARIA: it marks "this opens a
  // popup on activation"). TooltipTrigger does *not* set `aria-haspopup`, so
  // tooltip-wrapped chips remain static — match the existing pattern by adding
  // `tabIndex={0}` if you need keyboard focus there.
  const hasPopup = (rest as { 'aria-haspopup'?: string })['aria-haspopup'] !== undefined;
  const isInteractive = onClick !== undefined || hasPopup;
  if (isInteractive) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        data-slot="chip"
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'group/chip',
          chipBodyVariants({ color, variant, disabled, interactive: true }),
          className
        )}
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {bodyContents}
      </button>
    );
  }

  // Static chip — display only.
  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      data-slot="chip"
      data-disabled={disabled || undefined}
      className={cn(
        chipBodyVariants({ color, variant, disabled, interactive: false }),
        className
      )}
      {...(rest as React.HTMLAttributes<HTMLSpanElement>)}
    >
      {bodyContents}
    </span>
  );
});
Chip.displayName = 'Chip';

export { Chip };
